import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { ColDef, FirstDataRenderedEvent, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { GaAgGridService } from '@app/shared/ag-grid-components/ga-ag-grid.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaMessageService, IDialog } from '@app/shared/message/ga-message.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { EVENT_CLOSE_DIALOG, GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { BPProfilesService, BPProfilesViewCode } from '../../bp-profiles.service';
import { IColDef } from '@app/shared/report/report-grid.service';
import _ from 'underscore';
import { CellRendererComp } from '@app/shared/ag-grid-components/ga-ag-grid.component';
import { FormattingService } from '@app/shared/services/formatting.service';
import { IRichTextGrid, IRichTextRendererParams, RichTextType } from '@app/shared/ag-grid-components/ag-rich-text/ag-rich-text-renderer.component';
import { BPProfilesMailsService } from './bp-profiles-mails.service';
import { AG_PAGINATION_KEY, AgPaginationComponent, IAgPaginationParams } from '@app/shared/ag-grid-components/ag-pagination/ag-pagination.component';
import { PageChangedEvent } from 'ngx-bootstrap/pagination';
import { BPProfilesMailsDataService, IBPMail, IBPMailResponse } from './bp-profiles-mails-data.service';
import { Subscription } from 'rxjs';
import { GRID_BP_MAILS } from '@app/shared/app-grids';
import { UrlService } from '@app/shared/browser/url.service';

const gridHeightOffset:string = '40px;.ui-layout-north;.ui-layout-resizer-north;.filter-fields;.fixed-to-top-container;.new-mail-button';
const MAIL_RECIPIENTS_SPLITTER:string = ', ';

enum GridColumn {
  FROM = 'from',
  SUBJECT = 'subject',
  STATUS = 'status',
  DATE = 'date',
  TO = 'to'
}

export enum MailDialogType {
  NEW = 'new',
  EDIT = 'edit'
}

@Component({
  selector: 'bp-profiles-mails',
  template: `
    <div class="d-block clear margin-top-sm">
      <button class="btn btn-primary margin-bottom-sm new-mail-button" (click)="showMailDialog('${MailDialogType.NEW}', mailToUrl)" translate *ngIf="hasWritePermission" [disabled]="!mailToUrl">BPPROFILES.MAILS.NEW_MAIL</button>
      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" fillHeight="${gridHeightOffset}"></ga-ag-grid>
    </div>
  `
})
export class BPProfilesMailsComponent implements OnInit, OnDestroy {
  public gridId:string = GRID_BP_MAILS;
  public hasWritePermission:boolean;
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public mailToUrl:string;

  private eventSubscription:Subscription;
  private mailDialog:IDialog;

  constructor(
    private gaAgGridService:GaAgGridService,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    private authService:AuthService,
    private formattingService:FormattingService,
    private bpProfilesMailsService:BPProfilesMailsService,
    private bpProfilesMailsDataService:BPProfilesMailsDataService,
    private bpProfilesService:BPProfilesService,
    private eventService:GaEventService,
    private urlService:UrlService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.hasWritePermission = this.authService.viewWrite(BPProfilesViewCode.MAILS);
    // get mail to url
    this.getMailToUrl();
    // initialize grid
    this.gridInitialize();
    // register listener
    this.listen();
  }

  private listen():void {
    this.eventSubscription = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_CLOSE_DIALOG && this.mailDialog) {
        this.mailDialog.hide();
        this.mailDialog = undefined;
        this.loadData();
      }
    });
  }

  private getMailToUrl():void {
    this.mailToUrl = this.bpProfilesMailsService.formatNewMailUrl(this.urlService.bpMailToUrl, this.bpProfilesService.activeProfile?.companyCountryId?.toString());
  }

  private createColDefs():ColDef[] {
    const colDefs:IColDef[] = [
      {
        field: GridColumn.FROM,
        headerName: 'BPPROFILES.MAILS.GRID.FROM',
        autoHeight: true,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => _.map(params.value?.split(MAIL_RECIPIENTS_SPLITTER), (value:string) => {
            return {
              label: this.formattingService.formatMailAddress(value),
              prefixClass: 'badge bg-secondary',
              prefixLabel: this.translationService.instant('BPPROFILES.MAILS.GRID.FROM'),
              clickFn: this.onViewMail
            };
          })
        }
      },
      {
        field: GridColumn.SUBJECT,
        headerName: 'BPPROFILES.MAILS.GRID.SUBJECT',
        sortable: true,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.LINK,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, clickFn: this.onViewMail }]
        }
      },
      {
        field: GridColumn.STATUS,
        headerName: 'BPPROFILES.MAILS.GRID.STATUS',
        valueGetter: 'data.status && data.status.value',
        sortable: true,
        cellClass: 'text-center',
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.value, textClass: `badge ${this.bpProfilesMailsService.formatMailStatusLabelClass(params.value)}` }]
        }
      },
      {
        field: GridColumn.DATE,
        headerName: 'BPPROFILES.MAILS.GRID.DATE',
        cellClass: 'text-center',
        sortable: true,
        valueFormatter: (params:ValueFormatterParams):string => this.formattingService.formatDateTimeMedium(params.value),
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => [{ label: params.valueFormatted }]
        }
      },
      {
        field: GridColumn.TO,
        headerName: 'BPPROFILES.MAILS.GRID.TO',
        autoHeight: true,
        cellRendererParams: <IRichTextRendererParams> {
          type: RichTextType.TEXT,
          getValues: (params:IRichTextRendererParams):IRichTextGrid[] => {
            let values:IRichTextGrid[] = _.map(params.data?.to?.split(MAIL_RECIPIENTS_SPLITTER), (name:string) => {
              return {
                label: name,
                prefixClass: 'badge bg-secondary',
                prefixLabel: this.translationService.instant('BPPROFILES.MAILS.GRID.TO')
              };
            });

            values = values.concat(_.map(params.data?.cc?.split(MAIL_RECIPIENTS_SPLITTER), (name:string) => {
              return {
                label: name,
                prefixClass: 'badge bg-secondary',
                prefixLabel: this.translationService.instant('BPPROFILES.MAILS.GRID.CC')
              };
            }));

            return values;
          }
        }
      }
    ];

    _.forEach(colDefs, (colDef:IColDef) => {
      colDef.headerName = this.translationService.instant(colDef.headerName);
      colDef.cellRenderer = CellRendererComp.RICH_TEXT_RENDERER;
      colDef.resizable = true;
      colDef.cellRendererParams.showTooltip = true;
    });

    return colDefs;
  }

  private gridInitialize():void {
    this.gridOptions = {
      columnDefs: this.createColDefs(),
      suppressMovableColumns: true,
      onGridReady: ():void => {
        this.loadData();
      },
      onFirstDataRendered: (event:FirstDataRenderedEvent):void => {
        event.api.sizeColumnsToFit();
      },
      statusBar: {
        statusPanels: [
          {
            statusPanel: CellRendererComp.PAGINATION,
            key: AG_PAGINATION_KEY,
            statusPanelParams: <IAgPaginationParams>{
              onPageChanged: this.onPageChanged,
              maxSize: 10
            }
          }
        ]
      }
    };
    this.gridInit = true;
  }

  private onPageChanged = (event:PageChangedEvent):void => {
    this.loadData(event.page);
  };

  private loadData(page?:number):void {
    this.disablePagination(true);
    this.gaAgGridService.setGridLoading(this.gridId, true);
    this.bpProfilesMailsDataService.getBPMails(this.bpProfilesService.activeProfile?.id, page)
      .then((res:IBPMailResponse) => {
        this.gaAgGridService.setRowData(this.gridId, res.page.mailsList);
        // refresh pagination config
        this.gaAgGridService.getStatusBarComponentInstance<AgPaginationComponent>(this.gridId, AG_PAGINATION_KEY).refreshPagination({ totalItems: res.totalData, currentPage: res.currentPage });
      })
      .finally(() => {
        this.disablePagination(false);
        this.gaAgGridService.setGridLoading(this.gridId, false);
      });
  }

  private onViewMail = (event:Event, _onBusy:EventEmitter<boolean>, params:IRichTextRendererParams):void => {
    event.preventDefault();
    this.showMailDialog(MailDialogType.EDIT, (<IBPMail>params.data)?.editLink);
  };

  public showMailDialog(type:string, url:string):void {
    this.mailDialog = this.messageService.showGA1Dialog(
      type === MailDialogType.NEW ? 'BPPROFILES.MAILS.NEW_MAIL' : 'BPPROFILES.MAILS.VIEW_MAIL',
      url,
      {
        buttons: [
          { label: 'GA.BUTTONS.CANCEL', className: 'btn-default' }
        ]
      }
    );
  }

  private disablePagination(disabled:boolean){
    this.gaAgGridService.getStatusBarComponentInstance<AgPaginationComponent>(this.gridId, AG_PAGINATION_KEY).refreshPagination({ disabled });
  }

  public ngOnDestroy():void {
    this.eventSubscription?.unsubscribe();
  }
}